
import { BORDER_RADIUS, FLEX_CONTAINER_CENTERED, FLEX_CONTAINER_COLUMN_CENTERED, FLEX_GAP, H_100, PADDING, SHADOW, TEXT_COLOR, TEXT_WRAPPING, W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { createHtmlElement, empty, findNearestMatch, KeyboardInput, setHTMLElementContent } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { toPx } from "@cervval.js/common/src/tools/number.tools";
import { filterString } from "@cervval.js/common/src/tools/string.tools";
import { CjsIconFa } from "../icon/iconFa";
import { getIconFaName } from "../icon/iconFa.tools";
import { IconFA6 } from "../icon/tools/fontAwesome6IconList.tools";
import { CjsInput } from "../inputs/input";
import { PopupPosition, showPopup, ShowPopupReturn } from "../showPopup/showPopup";
import { CjsSmartTable, RenderCacheData, SmartTableCell, SmartTableOnFocusChangedEvent } from "./smartTable";

export const SMART_TABLE_SEARCH_CELL_RESULT = "cell-search-result";
export const SMART_TABLE_SEARCH_CELL = "cell-search";

export interface SmartTableSearchContext<ItemData> {

    /** Div to display search result */
    searchResultDiv: HTMLElement;

    /** Up search index icon */
    upSearchIcon: CjsIconFa;

    /** Down search index icon */
    downSearchIcon: CjsIconFa;

    /** Current search index */
    searchIndex: number;

    /** Current search text in input */
    searchText: string;

    /** Cells matching search result */
    matchingCells?: SmartTableCell<ItemData>[];

    /** Cell closest to search, focused after search */
    focusedCell?: SmartTableCell<ItemData>;
}

/** Search helper of smartTable */
export class SmartTableSearchHelper<ItemData> {

    /**
     * State or search
     * {true} research is occurring (popup is opened)
     */
    public isSearching = false;

    /** Store currently opened popup */
    public searchPopup: ShowPopupReturn | undefined;

    /** Store current search context, used to highlight cells researched after a table refresh */
    public searchContext: SmartTableSearchContext<ItemData> | undefined;

    constructor(protected _smartTable: CjsSmartTable<ItemData>) {
        this._install();
    }

    /**
     * Search row in smartTable of currently focused column
     */
    public _onKeydown(p: {
        event: KeyboardEvent,
        renderCacheData?: RenderCacheData,
        refresh?: boolean
    }): void {
        // Don't open popup is search is disabled
        if (!this._smartTable.options.search?.enable) { return; }

        const { event } = p;

        // Check is a cell was focused (used to retrieve column clicked)
        const focusedCell = this._smartTable.editionHelper.getFocusCell();
        if (!focusedCell) { return; }

        const isEnter = event.key === KeyboardInput.ENTER;

        // On enter key, go to next result
        if (isEnter && this.isSearching && this.searchContext) {

            // Default, increment index
            this.searchContext.searchIndex += 1;

            const isLastElement = this.searchContext.searchIndex === (this.searchContext.matchingCells ?? []).length;
            if (isLastElement) {// If end of searchResults, go to first result
                this.searchContext.searchIndex = 0;
            }

            this._search(this.searchContext.searchText, focusedCell);
            return;
        }

        // Test input to prevent popup on "ctrl", "shift", etc.
        const isLetterOrNumber = !event.ctrlKey && !event.shiftKey && !event.altKey && event.key.length === 1 && /[a-zA-Z0-9-_ ]/.test(event.key);

        // Open popup only if not already searching
        if (!this.isSearching && isLetterOrNumber) {
            this.openSearchPopup(focusedCell, event);
            this.isSearching = true;
        }

    }

    /** Open research popup */
    public openSearchPopup(focusedCell: SmartTableCell<ItemData>, event: KeyboardEvent): void {
        // Don't open popup is search is disabled
        if (!this._smartTable.options.search?.enable) { return; }

        const isEditable = this._smartTable.options.isCellEditable?.(focusedCell);
        if (!focusedCell.html || isEditable) { return; }

        // Create layout
        const searchInput = new CjsInput<string>({
            placeholderText: `${this._smartTable.i18n.searchLabel}...`
        });
        searchInput.classList.add(W_100);

        const closeSearchIcon = new CjsIconFa({
            name: getIconFaName(IconFA6.XMARK),
            classes: [TEXT_COLOR.SECONDARY, BORDER_RADIUS.ROUNDED]
        });

        const upSearchIcon = new CjsIconFa({
            name: getIconFaName(IconFA6.CARET_UP),
            classes: [TEXT_COLOR.SECONDARY, "smart-table-search", "up"]
        });
        const downSearchIcon = new CjsIconFa({
            name: getIconFaName(IconFA6.CARET_DOWN),
            classes: [TEXT_COLOR.SECONDARY, "smart-table-search", "down"]
        });

        const searchResultDiv = createHtmlElement("div", { cssClass: [TEXT_WRAPPING.NO_WRAP], style: { fontSize: toPx(12) } });

        this.searchContext = {
            searchText: "",
            searchIndex: 0,
            upSearchIcon,
            downSearchIcon,
            searchResultDiv
        };

        // On up btn click, decrement searchIndex
        upSearchIcon.clickEvent.on(() => {
            if (!this.searchContext) { return; }
            if (this.searchContext.searchIndex - 1 < 0) { return; }// index < 0 not allowed

            this.searchContext.searchIndex -= 1;
            this._search(searchInput.value ?? "", focusedCell);
        });

        // On up btn click, increment searchIndex
        downSearchIcon.clickEvent.on(() => {
            if (!this.searchContext) { return; }

            this.searchContext.searchIndex += 1;
            this._search(searchInput.value ?? "", focusedCell);
        });

        // Build popup content
        const content = createHtmlElement("div", {
            cssClass: [FLEX_CONTAINER_CENTERED, FLEX_GAP.GAP_3, PADDING.P_2],
            innerHTML: [
                searchInput,
                createHtmlElement("div", {
                    cssClass: [FLEX_CONTAINER_COLUMN_CENTERED, H_100],
                    innerHTML: [upSearchIcon, downSearchIcon]
                }),
                searchResultDiv,
                closeSearchIcon
            ]
        });


        // Get bound of cells to open popup on right of cell
        const focusedCellBounds = focusedCell.html.getBoundingClientRect();
        if (focusedCellBounds.x === 0 || focusedCellBounds.y === 0) { return; }// Focused cell not append in DOM

        // Open popup
        this.searchPopup = showPopup({
            parent: document.body,
            elementToShow: content,
            event,
            absolutePosition: {
                top: focusedCellBounds.y,
                left: focusedCellBounds.x + focusedCellBounds.width
            },
            popupPosition: PopupPosition.RIGHT_END,
            offset: [2, 0],
            cssPopup: {
                cssClass: [SHADOW.S12, BORDER_RADIUS.ROUNDED],
                style: {
                    minWidth: toPx(250)
                }
            },
            doNotCloseOnEnterKey: true,
            onPopupHide: () => {
                // Reset cells style before research
                this.resetSearchCellsStyle();

                // Reset context
                this.isSearching = false;
                this.searchPopup = undefined;
                this.searchContext = undefined;
            }
        });

        // Focus input to allow typing
        searchInput.focus();

        // On input event, apply search
        searchInput.inputEvent.on(e => {
            this._search(searchInput.value ?? "", focusedCell, { isFirstSearch: true });
        });

        // Add arrows up/down listeners
        searchInput.addEventListener("keydown", e => {
            const key = e.key;
            if (this.searchContext && (key === KeyboardInput.ARROW_UP || key === KeyboardInput.ARROW_DOWN)) {
                if (key === KeyboardInput.ARROW_UP) {
                    // Decrement index
                    this.searchContext.searchIndex -= 1;
                }
                if (key === KeyboardInput.ARROW_DOWN) {
                    // Increment index
                    this.searchContext.searchIndex += 1;
                }

                this._search(this.searchContext.searchText, focusedCell);

                // Don't move input cursor
                e.preventDefault();
            }
        });

        // Close popup on click
        closeSearchIcon.clickEvent.on(() => this.searchPopup?.removePopup("close"));
    }

    /** Perform search in table */
    protected _search(searchString: string, focusedCell: SmartTableCell<ItemData>, p?: { isFirstSearch?: boolean }): void {

        // Don't apply search is search option is disabled
        if (!this._smartTable.options.search?.enable) { return; }

        const searchContext = this.searchContext;
        if (!searchContext) { return; }

        searchContext.searchText = searchString;

        // Always reset cells style, disable icon, empty resultDiv. Their state will be updated
        this.resetSearchCellsStyle();
        empty(searchContext.searchResultDiv);
        searchContext.upSearchIcon.options.setAndRefreshIfChanged({ disabled: true });
        searchContext.downSearchIcon.options.setAndRefreshIfChanged({ disabled: true });

        const column = focusedCell.column;

        // Cells matching with research
        const matchingCells: SmartTableCell<ItemData>[] = [];

        const renderCacheData = {};
        // Search first matching cell
        for (const row of this._smartTable.displayableRows) {
            const columnCell = this._smartTable.getCellByRowAndColumn(row.uid, column.uid, renderCacheData);
            if (!columnCell) { continue; }

            const rowItemData = columnCell.rowItemData;

            if (column.filterComparator) {
                // Get custom cell value, use getDisplayString if defined
                if (column.filterComparator({ item: rowItemData, itemValue: columnCell.value, filterValue: searchString }, renderCacheData)) {
                    matchingCells.push(columnCell);
                }
            } else if (column.getDisplayString) {
                const cellDisplayString = column.getDisplayString(columnCell, renderCacheData);
                if (filterString(cellDisplayString, searchString, { startsWith: true, ...column.defaultFilterOptions })) {
                    matchingCells.push(columnCell);
                }
            } else {
                // Get custom cell value, use getDisplayString if defined
                const cellValue = columnCell.value;
                if (cellValue != null && !(cellValue instanceof HTMLElement)) {
                    // Get value parsed with with propertyConfig first
                    const parsedCellValue = this._smartTable.cellPropertyConfigTypeHandler(columnCell) ?? String(cellValue);
                    if (filterString(parsedCellValue, searchString, { startsWith: true, ...column.defaultFilterOptions })) {
                        matchingCells.push(columnCell);
                    }
                }
            }
        }

        // If firstSearch, find closest cell from focusedCell to start search
        if (p?.isFirstSearch) {
            const focusedCellIndex = focusedCell.row.globalIndex;
            const closestMatchingCell = matchingCells.reduce((prev, curr) => {
                return Math.abs(curr.row.globalIndex - focusedCellIndex) < Math.abs(prev.row.globalIndex - focusedCellIndex) ? curr : prev;
            }, focusedCell);
            searchContext.searchIndex = matchingCells.indexOf(closestMatchingCell);
        }

        // Handle negative index
        if (searchContext.searchIndex < 0) {
            searchContext.searchIndex = 0;
        }

        // Handle previous index upper than current search
        if (matchingCells.length && searchContext.searchIndex > matchingCells.length - 1) {
            searchContext.searchIndex = matchingCells.length - 1;
        }

        // Disable up/down icons if needed (limit searchIndex or no matching cells)
        searchContext.upSearchIcon.options.setAndRefreshIfChanged({ disabled: searchContext.searchIndex === 0 || matchingCells.length === 0 });
        searchContext.downSearchIcon.options.setAndRefreshIfChanged({ disabled: searchContext.searchIndex === matchingCells.length - 1 || matchingCells.length === 0 });

        // Useless to refresh if no cells are matching
        if (!matchingCells?.length) { return; }

        // Update result
        setHTMLElementContent(searchContext.searchResultDiv, `${searchContext.searchIndex + 1}/${matchingCells.length}`);

        // Get cell to focus
        const matchingCellAtIndex = matchingCells[searchContext.searchIndex];
        if (!matchingCellAtIndex?.rowItemData) { return; }

        // Store result in context
        searchContext.matchingCells = matchingCells;
        searchContext.focusedCell = matchingCellAtIndex;

        // Select row of matching cell
        this._smartTable.selectRows(matchingCellAtIndex.row.uid);
        this._smartTable.searchEvent.emit();

    }

    /** Refresh style of search cells */
    public refreshSearchCellsStyle(): void {
        // if no cell focused or search text, don't apply style
        if (!this.searchContext?.focusedCell || !this.searchContext.searchText) { return; }

        // If no cell focused, can't highlight it
        const focusedCell = this.searchContext?.focusedCell;
        if (!focusedCell) { return; }

        // Get upToDate rendered cell
        const focusedCellRendered = this._smartTable.getCellByRowAndColumn(focusedCell.row.uid, focusedCell.column.uid);
        focusedCellRendered?.html?.classList.add(SMART_TABLE_SEARCH_CELL);

        // Do not highlight search if option is enable or search is empty
        if (this._smartTable.options.search?.doNotHighlightSearch) { return; }

        // Highlight all matchingCell of search
        (this.searchContext.matchingCells ?? []).forEach(c => {
            // Get upToDate rendered cell
            const cr = this._smartTable.getCellByRowAndColumn(c.row.uid, c.column.uid);
            cr?.html?.classList.add(SMART_TABLE_SEARCH_CELL_RESULT);
        });

    }

    /** Refresh style of search cells */
    public resetSearchCellsStyle(): void {
        if (!this.searchContext?.focusedCell) { return; }
        const focusedCell = this.searchContext?.focusedCell;
        if (!focusedCell) { return; }

        const focusedCellRendered = this._smartTable.getCellByRowAndColumn(focusedCell.row.uid, focusedCell.column.uid);
        focusedCellRendered?.html?.classList.remove(SMART_TABLE_SEARCH_CELL, SMART_TABLE_SEARCH_CELL_RESULT);

        (this.searchContext.matchingCells ?? []).forEach(c => {
            const cr = this._smartTable.getCellByRowAndColumn(c.row.uid, c.column.uid);
            cr?.html?.classList.remove(SMART_TABLE_SEARCH_CELL_RESULT);
        });
    }

    /** End research (close popup) */
    protected _endSearch(): void {
        this.searchPopup?.removePopup("close");
    }

    /** Store focusChange listener */
    protected _focusChangeListener: EventListenerObject = {
        handleEvent: (e: CustomEvent<SmartTableOnFocusChangedEvent<ItemData>>) => {

            // Check if origin event isn't from popup himself, don't close popup in this case
            const originEvent = e.detail.event;
            const originEventTarget = originEvent?.target as HTMLElement | undefined;
            const popupDiv = this.searchPopup?.popup;
            if (originEventTarget && popupDiv) {
                const targetIsInsidePopup = findNearestMatch(originEventTarget, elt => elt === popupDiv);
                if (targetIsInsidePopup) {
                    return;
                }
            }

            // Retrieve old and new cells
            const oldCell = e.detail.oldCell;
            const newCell = e.detail.newCell;

            // Check if focusedCell has changed, don't endSearch if focusedCell hasn't changed => only a search input refresh
            const focusedCellHasChanged = oldCell?.column.uid !== newCell?.column.uid || oldCell?.row.uid !== newCell?.row.uid;
            if (!focusedCellHasChanged) { return; }

            // If other cell or no new cell focused, cancel research
            this._endSearch();
        }
    };

    /**
     * Install search helper on smartTable
     */
    protected _install(): void {
        // Listen to keyboard inputs
        document.body.addEventListener("keydown", this._keydownListener);

        // Listen to cells focus changes, used to close popup if other click on other element than popup
        this._smartTable.focusChangeEvent.on(this._focusChangeListener);
    }

    /**
     * Uninstall search helper
     */
    public uninstall(): void {
        document.body.addEventListener("keydown", this._keydownListener);
        this._smartTable.focusChangeEvent.off(this._focusChangeListener);
    }

    /** Store keydown listener */
    protected _keydownListener: EventListenerObject = {
        handleEvent: (e: KeyboardEvent) => {
            this._onKeydown({ event: e });
        }
    };
}